import type { Vivier } from '../partage/api/types';
import { useMenuOuvert } from '../partage/useMenuOuvert';
import { TOUS_LES_PROJETS } from '../partage/vocabulaire';
import { teinteDuProjet } from '../apparence';
import './ChoixDuDossier.css';

export type Pastille = Vivier['pastilles'][number];

/**
 * Le choix du dossier de « Maintenant » : une seule pastille, un menu (ticket 37).
 *
 * Huit pastilles étalées faisaient deux lignes avant même la première tâche : on se
 * perdait en ouvrant la page. La pastille dit ce qu'on regarde, le menu dit tout le reste,
 * y compris le cercle ambré d'un dossier qui attend le monde et sa légende.
 */
export function ChoixDuDossier({
  pastilles,
  choisi,
  onChoisir,
}: {
  pastilles: Pastille[];
  /**
   * Le dossier retenu, gardé tel qu'on l'a choisi : un filtre de « En attendant » peut
   * vider ses lignes et le faire sortir de la liste, la pastille doit quand même le nommer.
   */
  choisi: Pastille | null;
  onChoisir: (p: Pastille | null) => void;
}) {
  const menu = useMenuOuvert();
  const dossier = choisi?.id ?? null;
  const toutes = pastilles.reduce((somme, p) => somme + p.combien, 0);
  const unAttend = pastilles.some((p) => p.attendLeMonde);

  const choisir = (p: Pastille | null) => {
    onChoisir(p);
    menu.setOuvert(false);
  };

  return (
    <div className="choix-dossier" ref={menu.ref}>
      <button
        type="button"
        className="choix-dossier-pastille"
        data-tuto="pastille-dossiers"
        aria-haspopup="menu"
        aria-expanded={menu.ouvert}
        style={teinte(choisi)}
        onClick={() => menu.setOuvert(!menu.ouvert)}
      >
        <span className="choix-dossier-point" aria-hidden="true" />
        <span className="choix-dossier-nom">{choisi ? choisi.titre : TOUS_LES_PROJETS}</span>
        <span className="choix-dossier-fleche" aria-hidden="true">
          ▾
        </span>
      </button>

      {menu.ouvert && (
        <div className="choix-dossier-menu" role="menu">
          <button
            type="button"
            role="menuitemradio"
            aria-checked={dossier === null}
            className={dossier === null ? 'est-choisi' : ''}
            style={teinte(null)}
            onClick={() => choisir(null)}
          >
            <span className="choix-dossier-point" aria-hidden="true" />
            <span className="choix-dossier-nom">{TOUS_LES_PROJETS}</span>
            <span className="choix-dossier-combien">{toutes}</span>
          </button>
          {pastilles.map((p) => (
            <button
              key={p.id}
              type="button"
              role="menuitemradio"
              aria-checked={dossier === p.id}
              className={dossier === p.id ? 'est-choisi' : ''}
              style={teinte(p)}
              title={p.titre}
              data-tuto="dossier-du-menu"
              data-dossier={p.id}
              onClick={() => choisir(p)}
            >
              <span className="choix-dossier-point" aria-hidden="true" />
              <span className="choix-dossier-nom">{p.titre}</span>
              {p.attendLeMonde && (
                <span className="choix-dossier-monde" title="Ce dossier attend une réponse" />
              )}
              <span className="choix-dossier-combien">{p.combien}</span>
            </button>
          ))}
          {unAttend && (
            <p className="choix-dossier-legende">Le cercle ambré marque ce qui attend le monde</p>
          )}
        </div>
      )}
    </div>
  );
}

/** « tous les dossiers » n'a pas de teinte à lui : son point est à l'encre. */
function teinte(p: Pastille | null) {
  return {
    ['--teinte' as string]: p ? teinteDuProjet(p.couleur) : 'var(--encre)',
  };
}
